import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Lock, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIAL_RE = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?~`]/;

function passwordChecks(pw) {
  return {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    special: SPECIAL_RE.test(pw),
  };
}

export function PasswordStrengthMeter({ password }) {
  const checks = useMemo(() => passwordChecks(password || ''), [password]);
  const items = [
    { key: 'length', label: '8+ characters' },
    { key: 'upper', label: 'Uppercase letter' },
    { key: 'lower', label: 'Lowercase letter' },
    { key: 'digit', label: 'A digit' },
    { key: 'special', label: 'Special character' },
  ];
  const score = Object.values(checks).filter(Boolean).length;
  const barColor = score >= 5 ? 'bg-emerald-500' : score >= 3 ? 'bg-amber-500' : 'bg-rose-500';

  return (
    <div className="space-y-2 mt-2" data-testid="password-strength-meter">
      <div className="flex h-1.5 gap-1">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className={`flex-1 rounded ${score >= i ? barColor : 'bg-slate-200'}`} />
        ))}
      </div>
      <ul className="space-y-0.5 text-[11px]">
        {items.map(it => (
          <li key={it.key} className={`flex items-center gap-1.5 ${checks[it.key] ? 'text-emerald-700' : 'text-slate-500'}`}>
            {checks[it.key] ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
            {it.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const allOk = useMemo(() => {
    const c = passwordChecks(password);
    return c.length && c.upper && c.lower && c.digit && c.special && password === confirm;
  }, [password, confirm]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error('Invalid or missing token'); return; }
    if (!allOk) { toast.error('Password does not meet the requirements'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success('Password updated. Please log in.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed. Link may be expired.');
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md"><CardContent className="p-8 text-center">
          <XCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-2">Invalid reset link</h2>
          <p className="text-sm text-slate-600 mb-4">This reset link is missing a token or has expired.</p>
          <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline">Request a new one</Link>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-6 h-6 text-blue-600" />
            <h1 className="text-xl font-semibold">Set New Password</h1>
          </div>
          <p className="text-sm text-slate-600 mb-5">Choose a strong password. All requirements must be met.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="pw">New password</Label>
              <div className="relative">
                <Input id="pw" type={show ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)} required data-testid="reset-pw-input" />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordStrengthMeter password={password} />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm password</Label>
              <Input id="confirm" type={show ? 'text' : 'password'} value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" />
              {confirm && password !== confirm && (
                <p className="text-[11px] text-rose-600 mt-1">Passwords don&apos;t match</p>
              )}
            </div>
            <Button type="submit" disabled={busy || !allOk} className="w-full" data-testid="reset-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
